// Mẫu in "Danh sách học sinh thi" theo phòng (giống mẫu giấy nhà trường):
// A4 dọc, Times New Roman, mỗi phòng 1 trang — tiêu đề + "PHÒNG: xx", bảng
// TT | SBD | Họ và tên | Ngày sinh | Giới tính | Lớp | Số tờ/Mã đề | Ký nộp bài,
// thêm 1 dòng trống cuối bảng (chỉ khi phòng ≤ 24 HS), chân trang: số HS có mặt/vắng, số bài/số tờ,
// "Giám thị coi thi".
//   rooms: [{ name: "Phòng 16", students: [{ sbd, name, dob, gender, className }] }]

const esc = (x) => String(x ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Ngày sinh -> "dd/mm/yyyy" (chịu được yyyy-mm-dd, d/m/yyyy, dd/mm/yy, số
// serial Excel, Date). Không nhận ra thì giữ nguyên.
export function fmtDob(v) {
  if (v === null || v === undefined || v === "") return "";
  const p2 = (n) => String(n).padStart(2, "0");
  const out = (d, m, y) => (d >= 1 && d <= 31 && m >= 1 && m <= 12 ? `${p2(d)}/${p2(m)}/${y}` : null);
  if (v instanceof Date && !isNaN(v)) return out(v.getDate(), v.getMonth() + 1, v.getFullYear());
  const str = String(v).trim();
  if (/^\d{5}(\.\d+)?$/.test(str)) { // serial Excel (1 = 01/01/1900)
    const d = new Date(Date.UTC(1899, 11, 30) + Math.floor(Number(str)) * 86400000);
    return out(d.getUTCDate(), d.getUTCMonth() + 1, d.getUTCFullYear()) || str;
  }
  let m = str.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (m) return out(+m[3], +m[2], m[1]) || str;
  m = str.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/);
  if (m) { let y = +m[3]; if (y < 100) y += y <= 40 ? 2000 : 1900; return out(+m[1], +m[2], y) || str; }
  return str;
}

// "Phòng 16" -> "16"; tên phòng khác giữ nguyên.
const roomLabel = (name) => String(name || "").replace(/^\s*phòng\s*/i, "").trim();

// Mỗi phòng phải vừa 1 trang A4: vùng in cao 271mm, trừ tiêu đề + đầu bảng +
// chân trang còn ~215mm cho các dòng (25 dòng cao 8,6mm). Dòng trống cuối bảng chỉ thêm khi
// phòng có tối đa 24 HS. Phòng đông thì thu nhỏ chiều cao dòng (và cỡ chữ khi
// dòng thấp) thay vì tràn sang trang 2.
const ROWS_MM = 215;
const BLANK_ROW_MAX = 24;
const roomSizing = (rows) => {
  const rh = Math.min(8.6, Math.floor((ROWS_MM / rows) * 10) / 10);
  const fs = rh >= 6.5 ? 13.5 : rh >= 5.5 ? 12.5 : 11;
  return `--rh:${rh}mm;--fs:${fs}px`;
};

export function buildRoomListHtml({ title, rooms }) {
  const pages = rooms.map((r) => {
    const blank = r.students.length <= BLANK_ROW_MAX;
    const rows = r.students.map((s, i) => `<tr><td class="c">${i + 1}</td><td class="c">${esc(s.sbd)}</td><td class="l">${esc(s.name)}</td><td class="c">${esc(fmtDob(s.dob))}</td><td class="c">${esc(s.gender)}</td><td class="c">${esc(s.className)}</td><td></td><td></td></tr>`).join("");
    return `<section class="page" style="${roomSizing(r.students.length + (blank ? 1 : 0))}">
<h1>${esc(title)}</h1>
<h2>PHÒNG: ${esc(roomLabel(r.name))}</h2>
<table>
<colgroup><col style="width:6%"><col style="width:13%"><col style="width:26%"><col style="width:13%"><col style="width:10%"><col style="width:7%"><col style="width:9%"><col style="width:16%"></colgroup>
<thead><tr><th>TT</th><th>SBD</th><th>Họ và tên</th><th>Ngày sinh</th><th>Giới tính</th><th>Lớp</th><th>Số tờ/<br>Mã đề</th><th>Ký nộp bài</th></tr></thead>
<tbody>${rows}${blank ? "<tr><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td></td></tr>" : ""}</tbody>
</table>
<div class="foot">
<div class="left">Số HS có mặt: ……., số HS vắng: ………<br>Số bài thi: ……., số tờ giấy thi: ……………</div>
<div class="right">Giám thị coi thi</div>
</div>
</section>`;
  }).join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>
@page{size:A4 portrait;margin:14mm 12mm 12mm 16mm}
body{font-family:"Times New Roman",Times,serif;font-size:13.5px;color:#000;margin:0}
.page{page-break-after:always;font-size:var(--fs,13.5px)}
.page:last-child{page-break-after:auto}
h1{font-size:15px;font-weight:bold;text-align:center;margin:0 0 4px;white-space:normal;line-height:1.3}
h2{font-size:15px;font-weight:bold;text-align:center;margin:0 0 6px}
table{border-collapse:collapse;width:100%;table-layout:fixed}
th,td{border:1px solid #000;padding:0 5px;height:var(--rh,8.6mm);line-height:1.1;overflow:hidden;white-space:nowrap}
th{font-weight:bold;height:10mm;line-height:1.15}
td.c{text-align:center}td.l{text-align:left}
thead{display:table-header-group}
tr{page-break-inside:avoid}
table{page-break-inside:avoid}
.foot{display:flex;justify-content:space-between;margin-top:8mm;font-style:italic}
.foot .left{line-height:1.6}
.foot .right{font-weight:bold;padding-right:12mm}
</style></head><body>${pages}
<script>window.onload=function(){setTimeout(function(){window.print()},300)}</script></body></html>`;
}

export function openPrintWindow(html) {
  const w = window.open("", "_blank");
  if (!w) { window.alert("Trình duyệt chặn cửa sổ in — cho phép popup rồi bấm lại."); return; }
  w.document.open(); w.document.write(html); w.document.close();
}
